import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { 
  Code2, 
  Database, 
  Palette, 
  Smartphone, 
  Cloud, 
  Brain,
  Clock,
  Users,
  Star,
  ArrowRight,
  Play
} from 'lucide-react'

const courses = [
  {
    id: 1,
    title: 'Full-Stack React Mastery',
    category: 'Web Development',
    description: 'Build production-ready applications with React, Next.js, TypeScript, and Node.js.',
    image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&q=80',
    duration: '12 weeks',
    students: '12.5K',
    rating: 4.9,
    price: '$149',
    originalPrice: '$299',
    icon: Code2,
    color: '#5ed29c',
    tags: ['React', 'Next.js', 'TypeScript'],
  },
  {
    id: 2,
    title: 'Mobile App Development',
    category: 'Mobile',
    description: 'Create stunning cross-platform apps with React Native and Flutter.',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80',
    duration: '10 weeks',
    students: '8.2K',
    rating: 4.8,
    price: '$129',
    originalPrice: '$249',
    icon: Smartphone,
    color: '#4fd8ff',
    tags: ['React Native', 'Flutter', 'iOS'],
  },
  {
    id: 3,
    title: 'Data Science & ML',
    category: 'Data Science',
    description: 'Master Python, machine learning, and data visualization techniques.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
    duration: '16 weeks',
    students: '6.8K',
    rating: 4.9,
    price: '$199',
    originalPrice: '$399',
    icon: Brain,
    color: '#8b5cf6',
    tags: ['Python', 'ML', 'TensorFlow'],
  },
  {
    id: 4,
    title: 'Cloud & DevOps',
    category: 'DevOps',
    description: 'Deploy and scale applications with AWS, Docker, and Kubernetes.',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
    duration: '8 weeks',
    students: '5.4K',
    rating: 4.7,
    price: '$99',
    originalPrice: '$199',
    icon: Cloud,
    color: '#5ed29c',
    tags: ['AWS', 'Docker', 'K8s'],
  },
  {
    id: 5,
    title: 'UI/UX Design Systems',
    category: 'Design',
    description: 'Create beautiful, accessible interfaces with modern design principles.',
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80',
    duration: '6 weeks',
    students: '9.1K',
    rating: 4.8,
    price: '$79',
    originalPrice: '$159',
    icon: Palette,
    color: '#4fd8ff',
    tags: ['Figma', 'Design', 'UX'],
  },
  {
    id: 6,
    title: 'Database Architecture',
    category: 'Backend',
    description: 'Design scalable databases with PostgreSQL, MongoDB, and Redis.',
    image: 'https://images.unsplash.com/photo-1544383835-bda2bc66a55d?w=800&q=80',
    duration: '10 weeks',
    students: '4.3K',
    rating: 4.8,
    price: '$119',
    originalPrice: '$239',
    icon: Database,
    color: '#8b5cf6',
    tags: ['SQL', 'NoSQL', 'Redis'],
  },
]

const categories = ['All', 'Web Development', 'Mobile', 'Data Science', 'DevOps', 'Design', 'Backend']

export function Courses() {
  const containerRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, { once: true, margin: '-100px' })
  const [activeCategory, setActiveCategory] = useState('All')
  const [hoveredCourse, setHoveredCourse] = useState<number | null>(null)

  const filteredCourses = activeCategory === 'All' 
    ? courses 
    : courses.filter(c => c.category === activeCategory)

  return (
    <section
      ref={containerRef}
      id="courses"
      className="relative py-32 overflow-hidden"
    >
      <div className="absolute inset-0 aurora-bg opacity-20" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Explore Our <span className="text-gradient">Courses</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-10">
            Industry-leading curriculum designed by experts from Google, Meta, and Amazon
          </p>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <motion.button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-white text-background'
                    : 'glass text-white/70 hover:text-white'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Course Grid */}
        <motion.div 
          layout
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {filteredCourses.map((course, index) => (
              <motion.div
                key={course.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredCourse(course.id)}
                onMouseLeave={() => setHoveredCourse(null)}
                className="group relative"
              >
                <div className="relative glass rounded-2xl overflow-hidden h-full transition-all duration-500 hover:scale-[1.02]"
                  style={{
                    boxShadow: hoveredCourse === course.id ? `0 0 40px ${course.color}30` : 'none'
                  }}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={course.image} 
                      alt={course.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
                    
                    {/* Play Button */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: hoveredCourse === course.id ? 1 : 0, scale: hoveredCourse === course.id ? 1 : 0.8 }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </motion.div>
                    
                    {/* Category Badge */}
                    <div 
                      className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${course.color}30`, color: course.color }}
                    >
                      {course.category}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <course.icon className="w-5 h-5" style={{ color: course.color }} />
                      <div className="flex gap-1">
                        {course.tags.map((tag) => (
                          <span key={tag} className="text-xs text-white/40">#{tag}</span>
                        ))}
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gradient transition-all duration-300">
                      {course.title}
                    </h3>
                    <p className="text-white/60 text-sm mb-4 line-clamp-2">
                      {course.description}
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm text-white/50 mb-4">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {course.duration}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {course.students}
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400" />
                        {course.rating}
                      </div>
                    </div>
                    
                    {/* Price & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-white">{course.price}</span>
                        <span className="text-sm text-white/40 line-through">{course.originalPrice}</span>
                      </div>
                      <motion.button
                        className="flex items-center gap-1 text-sm font-medium transition-colors"
                        style={{ color: course.color }}
                        whileHover={{ x: 5 }}
                      >
                        Enroll Now <ArrowRight className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <motion.a
            href="#roadmaps"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full glass font-semibold text-white hover:bg-white/10 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            View All Courses
            <ArrowRight className="w-5 h-5" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  )
}